<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import categoryService from '@/services/service-desk/category.service'
import CategoryDrawer from '@/views/service-desk/drawer/master/CategoryDrawer.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

const searchQuery = ref('')
const isConfirmDialogVisible = ref(false)

definePage({
  meta: {
    name: 'master-category',
    subject: 'service_desk.menu',
    action: 'master_category',
  },
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const isCategoryDrawerVisible = ref(false)
const categoryDrawerData = ref()
const categoryDeleteData = ref()
const categoryDrawerType = ref('Add')

const refImportCategoryForm = ref()
const importCategoryFile = ref()
const importCategoryErrors = ref()
const isImportCategoryLoading = ref(false)
const isImportCategoryModal = ref(false)
const snackbar = useToast()


const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data){

    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order.toUpperCase()
}

const selectedParentCategory = ref()

const selectedDepartment = ref()


// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Code',
    key: 'code',
  },
  {
    title: 'Department',
    key: 'department_id',
  },
  {
    title: 'Parent Task',
    key: 'parent_id',
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const { data: departments, isFetching: isFetchingDepartment } = await useApi(
  createUrl("masters/departments", {
    query: {
      select: ["id", "name"],
    },
  }),
)

const {
  data: categoriesData,
  execute: fetchCategories,
} = useApi(createUrl('/service-desk/masters/categories', {
  query: computed(() => {
    const baseQuery = {
      q: searchQuery.value,
      sort: sortBy.value,
      order_by: orderBy.value,
      search: {
        department_id: selectedDepartment.value,
        parent_id: selectedParentCategory.value,
      },
      with: [
        'department:id,name',
        'parent',
      ],
    }

    if (itemsPerPage.value !== -1) {
      return {
        ...baseQuery,
        paged: 1,
        itemsPerPage: itemsPerPage.value,
        page: page.value,
      }
    }

    return baseQuery
  }),
}))

const categories = computed(() => {
  // When fetching all data
  if (itemsPerPage.value === -1) {
    return categoriesData.value || []
  }

  // Normal paginated response
  return categoriesData.value?.data || []
})

const totalCategories = computed(() => {
  // For "All" selection
  if (itemsPerPage.value === -1) {
    return categories.value.length
  }

  // For paginated results
  return categoriesData.value?.meta?.total || 0
})

const updateCategory = data => {
  if (data.id){
    categoryService.updateCategory(data.id, data, ()=>
    {
      fetchCategories()
      isCategoryDrawerVisible.value = false
      resetErrors()
    }, onError)

  }
}

const saveCategory = data => {
  categoryService.addCategory(data, ()=>
  {
    fetchCategories()
    isCategoryDrawerVisible.value = false
    resetErrors()

  }, onError)
}

const resetErrors = () => {
  errors.value = {
    name: [],
    code: [],
    status: [],
  }
}


const showCategory = (categoryData, type) => {
  categoryDrawerData.value = categoryData
  categoryDrawerType.value = type
  isCategoryDrawerVisible.value = true
}

const deleteCategory = async (confirm, data) => {
  if(data?.id && confirm ){
    await categoryService.deleteCategory(data.id, () => {
      fetchCategories()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = categoryData => {
  categoryDeleteData.value = categoryData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    categoryDeleteData.value = {}
  }
})

const { data: parentCategories, isFetching: isFetchingParentCategories } = await useApi(
  createUrl("/service-desk/masters/categories", {
    query: {
      parent_id: 'null',
    },
  }),
)

const submitImportCategory = async () => {
  if (!importCategoryFile.value) {
    importCategoryErrors.value = {
      file: ["Please select a file to import."],
    }

    return
  }
  isImportCategoryLoading.value = true
  importCategoryErrors.value = null // Clear previous errors

  try {
    await categoryService.import(
      {
        file: importCategoryFile.value,
      },
      () => {
        // onSuccess callback
        snackbar.success("Categories imported successfully.")
        isImportCategoryModal.value = false
        fetchCategories() // Refresh the list
        importCategoryFile.value = null // Reset file input
      },
      error => {
        // onError callback
        importCategoryErrors.value = error.data?.errors || {
          message:
            error.data?.message || "File upload failed. Please try again.",
        }
        snackbar.error(error.data?.message || "File upload failed.")
      },
    )
  } catch (e) {
    const errorData = e.data

    importCategoryErrors.value = errorData?.errors || {
      message: errorData?.message || "File upload failed. Please try again.",
    }
    snackbar.error(
      errorData?.message || "An unexpected error occurred during import.",
    )
    if (!importCategoryErrors.value && !errorData?.message)
      importCategoryErrors.value = { message: "Import submission failed." }
  } finally {
    isImportCategoryLoading.value = false
  }
}

const downloadSampleCategoryFile = async () => {
  await downloadFile(
    "/service-desk/masters/categories/sample-import-file/export",
    "categories-sample-excel.xlsx",
    () => {}, // success
    () => {}, // error
  )
}

watch(
  () => isImportCategoryModal.value,
  value => {
    if (value === false) {
      importCategoryFile.value = null
      importCategoryErrors.value = null
    }
  },
)
</script>

<template>
  <section>
    <!-- Page Header -->
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
      <div>
        <div class="text-h5">
          Task Management
        </div>
        <span class="text-body-2 text-medium-emphasis">
          Manage service desk task categories
        </span>
      </div>

      <div class="d-flex gap-3">
        <VBtn
          v-if="$can('create', 'service_desk.master_category')"
          variant="tonal"
          prepend-icon="tabler-download"
          @click="isImportCategoryModal = !isImportCategoryModal"
        >
          Import
        </VBtn>
        <VBtn
          v-if="$can('create', 'service_desk.master_category')"
          prepend-icon="tabler-plus"
          @click="showCategory({}, 'Add')"
        >
          Add New Task
        </VBtn>
      </div>
    </div>

    <VCard
      variant="flat"
      class="border"
    >
      <VCardText>
        <VRow align="center">
          <VCol
            cols="12"
            sm="4"
            md="3"
          >
            <AppTextField
              v-model="searchQuery"
              placeholder="Search by task..."
              density="compact"
            />
          </VCol>

          <VSpacer class="d-none d-sm-block" />

          <VCol
            cols="6"
            sm="4"
            md="3"
          >
            <AppSelect
              v-model="selectedDepartment"
              :loading="isFetchingDepartment"
              :items="departments"
              :item-title="(item) => item?.name"
              :item-value="(item) => item?.id"
              clearable
              placeholder="Departments"
            />
          </VCol>
          <VCol
            cols="6"
            sm="4"
            md="3"
          >
            <AppSelect
              v-model="selectedParentCategory"
              :loading="isFetchingParentCategories"
              :items="parentCategories"
              :item-title="(item) => item?.name"
              :item-value="(item) => item?.id"
              clearable
              placeholder="Parent Task"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="categories"
        :items-length="totalCategories"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <div
            class="d-flex align-center cursor-pointer"
            :categoryData="item"
            @click="showCategory(item,'View')"
          >
            <div class="d-flex flex-column">
              <h6 class="text-base">
                {{ item.name }}
              </h6>
            </div>
          </div>
        </template>

        <template #item.parent_id="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.parent?.name }}</span>
        </template>

        <template #item.department_id="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.department?.name }}</span>
        </template>

        <template #item.code="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.code }}</span>
        </template>
        <template #item.status="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.status?'Active':"Inactive" }}</span>
        </template>
        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('delete', 'service_desk.master_category')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn
            v-if="$can('update', 'service_desk.master_category')"
            @click="showCategory(item,'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>

    <CategoryDrawer
      v-model:isDrawerOpen="isCategoryDrawerVisible"
      :category-data="categoryDrawerData"
      :type="categoryDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="categoryDrawerType = $event"
      @update:category-data="updateCategory"
      @save-category="saveCategory"
    />

    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Category Deleted."
      confirm-title="Deleted!"
      :data="categoryDeleteData"
      @confirm="deleteCategory"
    />

    <!-- Import Category Modal -->
    <VDialog
      v-model="isImportCategoryModal"
      max-width="600"
      :persistent="isImportCategoryLoading"
    >
      <DialogCloseBtn
        size="small"
        :disabled="isImportCategoryLoading"
        @click="isImportCategoryModal = false"
      />
      <VCard>
        <VCardText class="pt-2">
          <h6 class="text-lg font-weight-medium mt-5">
            Import Categories
          </h6>
        </VCardText>

        <VCardText>
          <VForm ref="refImportCategoryForm">
            <VFileInput
              label="Upload File"
              prepend-icon="tabler-file-import"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              :disabled="isImportCategoryLoading"
              :error-messages="importCategoryErrors?.file"
              @change="
                (e) => {
                  importCategoryFile = e.target.files[0];
                }
              "
            />
          </VForm>
        </VCardText>

        <!-- General Errors -->
        <VCardText
          v-if="
            importCategoryErrors?.message &&
              !Array.isArray(importCategoryErrors)
          "
        >
          <VAlert
            color="error"
            variant="tonal"
            closable
            @click:close="importCategoryErrors.message = null"
          >
            {{ importCategoryErrors.message }}
          </VAlert>
        </VCardText>

        <!-- Validation Errors (row-specific from backend) -->
        <VCardText
          v-if="
            Array.isArray(importCategoryErrors) &&
              importCategoryErrors.length > 0
          "
        >
          <div
            class="d-flex flex-column gap-2"
            style="max-block-size: 200px; overflow-y: auto"
          >
            <VAlert
              v-for="(error, index) in importCategoryErrors"
              :key="index"
              color="error"
              variant="tonal"
              density="compact"
            >
              {{ error.row ? `Row ${error.row}: ` : ""
              }}{{ error.message || error }}
            </VAlert>
          </div>
        </VCardText>

        <!-- Loading Bar -->
        <VCardText
          v-if="isImportCategoryLoading"
          class="pt-0 pb-2"
        >
          <VProgressLinear
            indeterminate
            color="primary"
            height="6"
            rounded
            class="mb-1"
          />
        </VCardText>

        <!-- Action Buttons -->
        <VCardActions class="px-5 pb-5">
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="isImportCategoryLoading"
            @click="downloadSampleCategoryFile"
          >
            Download Sample
          </VBtn>
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="isImportCategoryLoading"
            @click="isImportCategoryModal = false"
          >
            Close
          </VBtn>
          <VBtn
            :loading="isImportCategoryLoading"
            :disabled="isImportCategoryLoading || !importCategoryFile"
            @click="submitImportCategory"
          >
            Import
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style lang="scss">
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
